import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Sparkles, Loader2, Share2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { consultationService, ConsultationResponse } from '../services/consultationApi';
import { deckFromSpread } from '../utils/deck';

const ConsultationDetailPage: React.FC = () => {
  const { consultationId } = useParams<{ consultationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [consultation, setConsultation] = useState<ConsultationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const renderJustified = (text: string, cls = "text-gray-200 text-sm leading-relaxed text-justify") =>
    text.split('\n').filter(l => l.trim()).map((line, i) => <p key={i} className={cls}>{line}</p>);

  // A Responses API (file_search) às vezes vaza marcador de citação inline com colchetes
  // full-width 【...】/《...》 no texto — nunca é conteúdo legítimo em português. Sem isso, uma
  // leitura antiga onde essa citação virou o "último parágrafo" (o que o backend usa como
  // Conselho Prático quando o baralho não usa cabeçalho de conselho) mostra a citação crua
  // em vez de um conselho de verdade. Corrigido na origem para leituras novas; isto conserta
  // as já salvas.
  const stripLeakedCitation = (text?: string | null) => {
    const cleaned = (text || '').replace(/[【《][^】》]{0,400}[】》]/g, '').trim();
    // Se sobrar só pontuação depois de tirar a citação (o conselho era só a citação vazada),
    // trata como vazio de verdade — não mostra a seção com um "." solto.
    return cleaned.replace(/[\s.,;:!?"'-]/g, '').length < 10 ? '' : cleaned;
  };

  // Helper para dividir "general_interpretation" em "Síntese da Tiragem" e "Interpretação Final"
  const parseGeneralInterpretation = (text?: string | null) => {
    if (!text) return { synthesis: '', final: '' };
    const normalized = text.replace(/\r\n/g, '\n');
    // Suporta títulos com ou sem emojis (🔶, 🧭). O ":" depois de "Interpretação Final"
    // é OPCIONAL na fronteira — a Aurora nem sempre repete a pergunta entre aspas no
    // cabeçalho; exigir o ":" fazia esse regex não achar a fronteira, e a Síntese
    // engolia a Interpretação Final inteira (que reaparecia duplicada mais abaixo,
    // já que o finalRegex abaixo sempre encontrava o cabeçalho corretamente).
    const synthRegex = /(?:^|\n)\s*(?:#{1,6}\s*|>\s?)?[*_]{0,2}(?:🔶\s*)?Síntese da Tiragem[*_]{0,2}\s*:?\s*\n([\s\S]*?)(?=\n\s*(?:#{1,6}\s*|>\s?)?[*_]{0,2}(?:🧭\s*)?Interpretação Final[*_]{0,2}\s*:?\s*(?:"[^"]*")?\s*(?:\n|$)|$)/i;
    const finalRegex = /(?:#{1,6}\s*|>\s?)?[*_]{0,2}(?:🧭\s*)?Interpretação Final[*_]{0,2}\s*:?\s*(?:\"[^\"]*\"|)\s*\n?([\s\S]*)$/i;
    const synthMatch = normalized.match(synthRegex);
    const finalMatch = normalized.match(finalRegex);
    const synthesis = synthMatch?.[1]?.trim() || '';
    let final = finalMatch?.[1]?.trim() || (synthesis ? '' : normalized.trim());
    // A Aurora às vezes reemite o cabeçalho "Interpretação Final" no meio do bloco
    // (padrão comum de LLM). Remove o cabeçalho repetido, mantendo a prosa que o segue,
    // pra não renderizar o título duas vezes (uma como <h4>, outra como texto literal).
    final = final
      .replace(/(^|\n)[ \t]*(?:#{1,6}[ \t]*|>[ \t]?)?[*_]{0,2}(?:🧭[ \t]*)?Interpreta[çc][ãa]o Final[*_]{0,2}[ \t]*:?[ \t]*(?:"[^"]*")?[ \t]*/gi, '$1')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    return { synthesis, final };
  };

  useEffect(() => {
    if (consultationId) {
      loadConsultation();
    }
  }, [consultationId]);

  const loadConsultation = async () => {
    if (!consultationId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await consultationService.getConsultation(consultationId);
      setConsultation(response);
    } catch (error: any) {
      console.error('Erro ao carregar consulta:', error);
      setError(error.message || 'Erro ao carregar consulta');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleShare = async () => {
    if (navigator.share && consultation) {
      try {
        await navigator.share({
          title: `Consulta — ${deckFromSpread(consultation.spread_type).label}`,
          text: `Consulta: "${consultation.question}"`,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Erro ao compartilhar:', error);
      }
    } else {
      // Fallback: copiar URL para clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copiado para a área de transferência!');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-mystical flex items-center justify-center">
        <div className="text-center text-white">
          <h2 className="text-2xl font-mystical mb-4">Acesso negado</h2>
          <p className="mb-4">Você precisa estar logado para ver esta consulta.</p>
          <Link 
            to="/login" 
            className="px-6 py-3 bg-gradient-to-r from-gold-600 to-gold-700 text-white rounded-lg hover:from-gold-700 hover:to-gold-800 transition-all"
          >
            Fazer Login
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-mystical flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-12 h-12 animate-spin text-gold-400 mx-auto mb-4" />
          <h2 className="text-2xl font-mystical mb-4">Carregando consulta...</h2>
        </div>
      </div>
    );
  }

  if (error || !consultation) {
    return (
      <div className="min-h-screen bg-gradient-mystical flex items-center justify-center">
        <div className="text-center text-white max-w-md mx-auto">
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-6">
            <h2 className="text-2xl font-mystical mb-4">Erro ao carregar</h2>
            <p className="text-red-300 mb-6">{error || 'Consulta não encontrada'}</p>
            <div className="space-x-4">
              <button
                onClick={loadConsultation}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Tentar Novamente
              </button>
              <Link
                to="/consultation-history"
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Voltar ao Histórico
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Interpretacao parseada
  const parsedInterpretation = parseGeneralInterpretation(consultation?.general_interpretation);

  return (
    <div className="min-h-screen bg-gradient-mystical text-white">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-white/20">
        <Link 
          to="/consultation-history" 
          className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Voltar ao Histórico</span>
        </Link>
        <h1 className="text-2xl font-mystical font-bold">CONSULTA DETALHADA</h1>
        <button
          onClick={handleShare}
          className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
        >
          <Share2 size={20} />
          <span>Compartilhar</span>
        </button>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Cabeçalho da consulta */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="text-gold-400" size={24} />
            <span className="text-gray-300">
              {formatDate(consultation.created_at)}
            </span>
            {deckFromSpread(consultation.spread_type).key !== 'aurora' && (
              <span className="px-4 py-2 bg-gold-600/40 text-gold-100 text-sm rounded-full">
                {deckFromSpread(consultation.spread_type).label}
              </span>
            )}
            <span className="px-4 py-2 bg-mystical-600/50 text-mystical-200 text-sm rounded-full">
              {consultation.spread_name}
            </span>
          </div>
          
          <h2 className="text-3xl font-mystical font-bold text-white mb-4">
            Sua Pergunta
          </h2>
          <div className="bg-gold-500/20 border border-gold-400/30 rounded-lg p-6">
            <p className="text-xl text-gold-100 italic text-center">
              "{consultation.question}"
            </p>
          </div>
        </motion.div>

        {/* Interpretação geral — só quando não há cartas (perguntas livres/skills da Aurora).
            Leituras de tarot com cartas usam apenas o bloco "Interpretação Completa" abaixo,
            igual à tela de leitura, para não duplicar a síntese. */}
        {(!consultation.cards || consultation.cards.length === 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 mb-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="text-gold-400" size={24} />
              <h3 className="text-2xl font-mystical font-bold text-white">
                Interpretação Geral
              </h3>
            </div>
            <div className="prose prose-invert max-w-none">
              <div className="space-y-1">
                {renderJustified(parsedInterpretation.synthesis || consultation?.general_interpretation || '', "text-gray-200 leading-relaxed text-justify")}
              </div>
            </div>
          </motion.div>
        )}

        {/* Interpretações individuais das cartas */}
        {consultation.cards && consultation.cards.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 mb-8"
          >
            <h3 className="text-2xl font-mystical font-bold text-gold-300 text-center mb-8">
              Interpretação Individual das Cartas
            </h3>
            <div className="grid gap-6">
              {consultation.cards.map((cardInterpretation, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="bg-white/5 rounded-lg p-6 border border-white/10"
                >
                  <div className="flex items-start gap-4 mb-4">
                    {cardInterpretation.image_url && (
                      <img
                        src={cardInterpretation.image_url}
                        alt={cardInterpretation.card_name}
                        className={`w-24 object-contain rounded-lg border-2 border-gold-400/50 shadow-lg flex-shrink-0${cardInterpretation.is_reversed ? ' rotate-180' : ''}`}
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-3 h-3 bg-gold-400 rounded-full flex-shrink-0"></div>
                        <h4 className="text-xl font-semibold text-gold-300">
                          {cardInterpretation.position}: {cardInterpretation.card_name}
                          {cardInterpretation.is_reversed && (
                            <span className="text-red-300 text-sm ml-3">(Carta Invertida)</span>
                          )}
                        </h4>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <p className="text-gray-400 text-sm font-medium mb-1">Significado da Carta:</p>
                          <p className="text-gray-300">
                            {cardInterpretation.meaning}
                          </p>
                        </div>

                        <div>
                          <p className="text-gray-400 text-sm font-medium mb-1">Interpretação para Sua Situação:</p>
                          <p className="text-gray-200 leading-relaxed text-justify">
                            {cardInterpretation.interpretation}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Interpretação Completa — só para consultas de tarot com cartas */}
        {consultation.general_interpretation && consultation.cards && consultation.cards.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-r from-gold-500/20 to-mystical-500/20 rounded-2xl p-8 border border-gold-400/30 mb-8"
          >
            <div className="text-center">
              <div className="flex items-center justify-center gap-3 mb-6">
                <Sparkles className="text-gold-400" size={28} />
                <h3 className="text-2xl font-mystical font-bold text-gold-300">
                  Interpretação Completa
                </h3>
                <Sparkles className="text-gold-400" size={28} />
              </div>
              <div className="bg-white/10 rounded-lg p-6 text-left">
                {/* Síntese da Tiragem */}
                {parsedInterpretation.synthesis && (
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gold-300 mb-2">Síntese da Tiragem</h4>
                    <div className="space-y-1">{renderJustified(parsedInterpretation.synthesis)}</div>
                  </div>
                )}
                {/* Interpretação Final */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gold-300 mb-2">Interpretação Final</h4>
                  <div className="space-y-1">{renderJustified(parsedInterpretation.final || consultation.general_interpretation)}</div>
                </div>
                {/* Conselho Prático */}
                {stripLeakedCitation(consultation.advice) && (
                  <div>
                    <h4 className="text-lg font-semibold text-gold-300 mb-2">Conselho Prático</h4>
                    <div className="space-y-1">{renderJustified(stripLeakedCitation(consultation.advice))}</div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Perguntas sobre esta leitura */}
        {consultation.follow_up_qa && consultation.follow_up_qa.length > 0 && (
          <div className="mb-8 space-y-3">
            {consultation.follow_up_qa.map((qa, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * i }}
                className="p-4 sm:p-6 bg-gradient-to-r from-gold-500/10 to-amber-500/10 rounded-xl border border-gold-400/20"
              >
                <p className="text-gold-300 text-sm font-semibold mb-2">❓ {qa.question}</p>
                <div className="space-y-1">{renderJustified(qa.answer)}</div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Análise Aprofundada */}
        {consultation.deeper_reading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8 p-4 sm:p-6 bg-gradient-to-r from-indigo-500/15 to-purple-500/15 rounded-xl border border-indigo-400/30"
          >
            <h3 className="text-base sm:text-lg font-mystical font-bold text-indigo-300 mb-3 text-center">
              ✨ Análise Aprofundada
            </h3>
            <div className="space-y-1">{renderJustified(consultation.deeper_reading)}</div>
          </motion.div>
        )}

        {/* Ações */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center space-y-4"
        >
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/dashboard"
              className="px-8 py-3 bg-gradient-to-r from-gold-600 to-gold-700 text-white rounded-lg hover:from-gold-700 hover:to-gold-800 transition-all transform hover:scale-105 flex items-center gap-2"
            >
              <Sparkles size={20} />
              Nova Consulta
            </Link>
            
            <Link
              to="/consultation-history"
              className="px-8 py-3 bg-gradient-to-r from-mystical-600 to-mystical-700 text-white rounded-lg hover:from-mystical-700 hover:to-mystical-800 transition-all transform hover:scale-105"
            >
              Ver Histórico
            </Link>
          </div>
          
          <p className="text-gray-400 text-sm">
            {deckFromSpread(consultation.spread_type).key === 'aurora'
              ? 'Lembre-se: a orientação da Aurora é um apoio, mas você é o autor de sua própria história.'
              : 'Lembre-se: as cartas são um guia, mas você é o autor de sua própria história.'}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default ConsultationDetailPage;